"use client"

import { useCallback, useEffect, useRef, useState, startTransition } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { cn } from "@/lib/utils"

export function NavigationProgress() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isNavigating, setIsNavigating] = useState(false)
  const [progress, setProgress] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearNavigationTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Track navigation completion
  useEffect(() => {
    clearNavigationTimers()
    // When pathname or search params change, navigation is complete
    setIsNavigating(false)
    setProgress(100)

    // Reset progress after animation completes
    const timeout = setTimeout(() => {
      setProgress(0)
    }, 300)

    return () => clearTimeout(timeout)
  }, [clearNavigationTimers, pathname, searchParams])

  // Listen for click events on links to detect navigation start
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

      const target = e.target as HTMLElement
      const anchor = target.closest("a")

      if (!anchor) return

      const href = anchor.getAttribute("href")
      if (!href) return

      if (shouldIgnoreProgressForLink(anchor, href)) {
        return
      }

      clearNavigationTimers()

      // Start navigation progress
      startTransition(() => {
        setIsNavigating(true)
        setProgress(20)
      })

      // Simulate progress increments
      let currentProgress = 20
      intervalRef.current = setInterval(() => {
        currentProgress += Math.random() * 15
        if (currentProgress >= 90) {
          currentProgress = 90
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
        }
        setProgress(currentProgress)
      }, 200)

      // Set a max timeout to prevent infinite loading state
      timeoutRef.current = setTimeout(() => {
        clearNavigationTimers()
        setIsNavigating(false)
        setProgress(0)
      }, 10000)
    }

    document.addEventListener("click", handleClick)
    return () => {
      document.removeEventListener("click", handleClick)
      clearNavigationTimers()
    }
  }, [clearNavigationTimers])

  if (progress === 0) return null

  return (
    <div className="fixed top-0 left-0 right-0 z-[200] h-1 bg-transparent">
      <div
        className={cn(
          "h-full bg-primary transition-all duration-300 ease-out",
          isNavigating ? "opacity-100" : "opacity-0"
        )}
        style={{ width: `${progress}%` }}
      />
      {/* Glow effect */}
      <div
        className={cn(
          "absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-primary/50 to-transparent blur-sm transition-opacity duration-300",
          isNavigating && progress > 0 ? "opacity-100" : "opacity-0"
        )}
        style={{ transform: `translateX(-${100 - progress}%)` }}
      />
    </div>
  )
}

function shouldIgnoreProgressForLink(anchor: HTMLAnchorElement, href: string) {
  const trimmedHref = href.trim()

  if (
    !trimmedHref ||
    trimmedHref.startsWith("#") ||
    anchor.hasAttribute("download") ||
    anchor.target === "_blank" ||
    /^(mailto|tel|blob|data|javascript):/i.test(trimmedHref)
  ) {
    return true
  }

  let url: URL
  try {
    url = new URL(trimmedHref, window.location.href)
  } catch {
    return true
  }

  if (url.origin !== window.location.origin || url.pathname.startsWith("/api/")) {
    return true
  }

  return url.pathname === window.location.pathname && url.search === window.location.search
}
